import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { siteEmail, siteName } from "@/mocks/guawa";
import useSeo from "@/hooks/useSeo";

const SUBMIT_URL = "https://readdy.ai/api/form/daeo1dhtto0ceqqnh13g";

const infoCards = [
  { icon: "ri-mail-send-line", title: "投稿 / 供稿", desc: "有见闻或想补充的信息？给我们留言，编辑将择优整理上架。", link: "投稿通道" },
  { icon: "ri-feedback-line", title: "意见反馈", desc: "页面、功能或讨论氛围上有任何建议，我们都非常欢迎。", link: "在线反馈" },
  { icon: "ri-user-add-line", title: "加入我们", desc: "认同我们的理念？如果你也是资深瓜友，欢迎聊聊。", link: "合作洽谈" },
];

export default function Contact() {
  useSeo({
    title: "联系我们 / 投稿 - 今日曝瓜 | 反馈与投稿通道",
    description:
      "通过今日曝瓜联系页投稿、反馈意见或洽谈合作。分享你的见闻与观点，和我们一起维护理性又有趣的吃瓜社区。",
  });

  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：读取诱饵字段，非空则静默走通用成功反馈
    const bait = (fd.get("website_alt") as string)?.trim() ?? "";
    if (bait) {
      setStatus("success");
      return;
    }

    // 剔除空值字段
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value.trim());
      }
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(parsed?.meta?.message || parsed?.meta?.detail || "提交失败，请稍后重试或更换联系方式。");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎开了小差，请检查后重新提交。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />

      <section className="border-b border-cream-200 bg-gradient-to-r from-cream-100 via-white to-cream-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-ink-400" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="font-medium text-ink-600 whitespace-nowrap">联系我们</span>
          </nav>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-ink-900 md:text-4xl">投稿与联系</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-500">
            欢迎分享见闻、提出建议或洽谈合作。我们通常在 24 小时内回复，重要消息会优先处理。
          </p>
        </div>
      </section>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          {/* 左侧信息 */}
          <aside className="flex flex-col gap-4">
            {infoCards.map((c) => (
              <div key={c.title} className="rounded-2xl border border-cream-200 bg-white p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-2xl text-primary-600">
                  <i className={c.icon}></i>
                </span>
                <h2 className="mt-3 text-sm font-bold text-ink-900">{c.title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">{c.link}</span>
              </div>
            ))}

            <div className="rounded-2xl bg-ink-800 p-5 text-cream-100">
              <h2 className="flex items-center gap-2 text-sm font-bold text-white">
                <i className="ri-time-line text-accent-400"></i> 响应时效
              </h2>
              <ul className="mt-3 flex flex-col gap-2 text-xs text-cream-200/80">
                <li className="flex justify-between"><span>普通投稿反馈</span><span className="text-white">24 小时内</span></li>
                <li className="flex justify-between"><span>重要线索</span><span className="text-accent-300">优先处理</span></li>
                <li className="flex justify-between"><span>合作洽谈</span><span className="text-white">48 小时内</span></li>
              </ul>
              <a href={`mailto:${siteEmail}`} className="mt-4 flex items-center gap-2 text-sm text-accent-300 hover:text-accent-200 whitespace-nowrap">
                <i className="ri-mail-send-line"></i> {siteEmail}
              </a>
            </div>
          </aside>

          {/* 右侧表单 */}
          <div className="rounded-2xl border border-cream-200 bg-white p-6 md:p-8">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-ink-900">
              <i className="ri-edit-2-line text-primary-500"></i> 填写表单
            </h2>
            <p className="mt-1.5 text-sm text-ink-500">带 * 为必填项，你的信息仅用于本站与你取得联系。</p>

            <form
              className="mt-6 flex flex-col gap-5"
              data-readdy-form="guawa-contact"
              onSubmit={handleSubmit}
              noValidate
            >
              {/* 反垃圾诱饵字段 */}
              <input
                type="text"
                name="website_alt"
                className="field-aux"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="nickname" className="mb-1.5 block text-sm font-semibold text-ink-700">您的称呼 *</label>
                  <input
                    id="nickname"
                    name="nickname"
                    required
                    maxLength={30}
                    placeholder="怎么称呼您"
                    className="w-full rounded-lg border border-cream-200 bg-cream-50 px-3.5 py-2.5 text-sm text-ink-800 outline-none transition-colors focus:border-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-ink-700">联系邮箱 *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    placeholder="方便我们回复您"
                    className="w-full rounded-lg border border-cream-200 bg-cream-50 px-3.5 py-2.5 text-sm text-ink-800 outline-none transition-colors focus:border-primary-300"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="category" className="mb-1.5 block text-sm font-semibold text-ink-700">联系主题 *</label>
                <select
                  id="category"
                  name="category"
                  required
                  defaultValue=""
                  className="w-full rounded-lg border border-cream-200 bg-cream-50 px-3.5 py-2.5 text-sm text-ink-800 outline-none transition-colors focus:border-primary-300"
                >
                  <option value="" disabled>请选择主题</option>
                  <option value="投稿线索">投稿 / 提供线索</option>
                  <option value="意见反馈">意见 / 功能反馈</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-ink-700">留言内容 *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请尽量描述清楚您的来意、线索或建议（500 字以内）……"
                  className="w-full resize-none rounded-lg border border-cream-200 bg-cream-50 px-3.5 py-2.5 text-sm leading-relaxed text-ink-800 outline-none transition-colors focus:border-primary-300"
                ></textarea>
              </div>

              {status === "success" && (
                <div className="flex items-start gap-3 rounded-xl bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg"></i>
                  已收到你的留言，感谢支持！我们会在 24 小时内与你联系。
                </div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  <i className="ri-error-warning-line mt-0.5 text-lg"></i>
                  {formError}
                </div>
              )}

              <div className="flex flex-col items-start justify-between gap-3 border-t border-cream-100 pt-5 sm:flex-row sm:items-center">
                <p className="text-xs text-ink-400">提交即表示同意我们的内容与隐私规范。</p>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> 提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i> 提交 {siteName} 表单
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* ContactPage 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系我们 - " + siteName,
            url: "https://ebimall.cn/contact",
            inLanguage: "zh-CN",
          }),
        }}
      />

      <Footer />
      <BackToTop />
    </div>
  );
}